import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, Paper, List, ListItem, ListItemText, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, CircularProgress, Alert } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate, useParams } from 'react-router-dom';
import { collection, addDoc, onSnapshot, query, orderBy, Timestamp } from 'firebase/firestore';
import { firestore } from '../config/firebase';
import type { Timestamp as FirestoreTimestamp } from 'firebase/firestore';

interface DesignSprint {
  id: string;
  name: string;
  status: 'Active' | 'Completed' | 'Archived';
  createdAt: string;
  updatedAt: string;
}

const DesignSprintsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  const [sprints, setSprints] = useState<DesignSprint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSprintName, setNewSprintName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    const q = query(collection(firestore, `teams/${teamId}/designSprints`), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snapshot) => {
      setSprints(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as DesignSprint)));
      setLoading(false);
    }, (err) => {
      setError('Failed to load sprints.');
      setLoading(false);
    });
    return () => unsub();
  }, [teamId]);

  const handleOpenCreate = () => setCreateOpen(true);
  const handleCloseCreate = () => {
    setCreateOpen(false);
    setNewSprintName('');
  };
  const handleCreateSprint = async () => {
    if (!teamId || !newSprintName.trim()) return;
    setCreating(true);
    try {
      const now = Timestamp.now();
      const docRef = await addDoc(collection(firestore, `teams/${teamId}/designSprints`), {
        name: newSprintName.trim(),
        status: 'Active',
        createdAt: now,
        updatedAt: now,
      });
      handleCloseCreate();
      setCreating(false);
      navigate(`/teams/${teamId}/design-sprints/${docRef.id}`);
    } catch (err) {
      setError('Failed to create sprint.');
      setCreating(false);
    }
  };

  const handleSprintClick = (sprintId: string) => {
    navigate(`/teams/${teamId}/design-sprints/${sprintId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Completed':
        return 'bg-blue-100 text-blue-800';
      case 'Archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
        return (
          <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">A</span>
          </div>
        );
      case 'Completed':
        return (
          <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">C</span>
          </div>
        );
      case 'Archived':
        return (
          <div className="w-12 h-12 bg-gray-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">A</span>
          </div>
        );
      default:
        return (
          <div className="w-12 h-12 bg-gray-500 rounded-xl flex items-center justify-center">
            <span className="text-white font-bold text-lg">D</span>
          </div>
        );
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Unknown date';
    
    try {
      let date: Date;
      if (typeof dateString === 'object' && 'toDate' in dateString) {
        date = (dateString as FirestoreTimestamp).toDate();
      } else {
        date = new Date(dateString);
      }
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600 font-['Inter']">Loading design sprints...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Enhanced Header with gradient background */}
      <header className="relative bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700 rounded-b-3xl pb-8 pt-6 px-6">
        {/* Back button */}
        <button
          onClick={() => navigate(`/teams/${teamId}`)}
          className="absolute top-6 left-6 p-2 bg-white/20 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Title and description */}
        <div className="mt-12 text-center">
          <h1 className="text-3xl font-bold text-white font-['Poppins'] mb-2">Design Sprints</h1>
          <p className="text-white/80 text-lg font-['Inter']">
            Create and manage your team's design sprint sessions
          </p>
        </div>
      </header>

      <main className="flex-1 flex flex-col p-6">
        {/* Enhanced Toolbar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900 font-['Poppins']">
                All Sprints ({sprints.length})
              </h2>
            </div>
            
                         <button
               onClick={handleOpenCreate}
               className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-3 rounded-xl hover:shadow-lg transition-all duration-200 font-medium font-['Inter'] flex items-center gap-2"
             >
               <AddIcon className="w-5 h-5" />
               CREATE NEW SPRINT
             </button>
          </div>
        </div>

        {error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-800 font-['Inter']">{error}</p>
          </div>
        ) : sprints.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2 font-['Poppins']">No design sprints yet</h3>
            <p className="text-gray-600 mb-4 font-['Inter']">Create your first design sprint to get started.</p>
            <p className="text-sm text-gray-500 font-['Inter']">Click the + button to create your first sprint.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sprints.map(sprint => (
              <div
                key={sprint.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-200 cursor-pointer group"
                onClick={() => handleSprintClick(sprint.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  {getStatusIcon(sprint.status)}
                  <button
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-all duration-200 opacity-0 group-hover:opacity-100"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSprintClick(sprint.id);
                    }}
                  >
                    <ArrowForwardIcon className="w-5 h-5" />
                  </button>
                </div>
                
                <h3 className="text-lg font-semibold text-gray-900 mb-2 font-['Poppins'] line-clamp-2">
                  {sprint.name}
                </h3>
                
                <div className="mb-4">
                  <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(sprint.status)}`}>
                    {sprint.status}
                  </span>
                </div>
                
                <div className="text-sm text-gray-500 font-['Inter'] space-y-1">
                  <p>Created: {formatDate(sprint.createdAt)}</p>
                  <p>Updated: {formatDate(sprint.updatedAt)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Enhanced Footer */}
      <footer className="text-center text-sm text-gray-500 py-6 font-['Inter']">
        © {new Date().getFullYear()} StyleSync - Collaborative Design Platform
      </footer>

      {/* Enhanced Create Dialog */}
      <Dialog 
        open={createOpen} 
        onClose={handleCloseCreate} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 600,
          fontSize: '1.5rem',
          color: '#1f2937',
          pb: 1
        }}>
          Create New Design Sprint
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Sprint Name"
            value={newSprintName}
            onChange={e => setNewSprintName(e.target.value)}
            fullWidth
            autoFocus
            disabled={creating}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Inter, sans-serif',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6'
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#3b82f6',
                  borderWidth: '2px'
                }
              },
              '& .MuiInputLabel-root': {
                fontFamily: 'Inter, sans-serif',
                '&.Mui-focused': {
                  color: '#3b82f6'
                }
              }
            }}
            placeholder="Enter sprint name..."
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseCreate} 
            disabled={creating}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateSprint} 
            variant="contained" 
            disabled={creating || !newSprintName.trim()}
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              color: '#ffffff',
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              borderRadius: '12px',
              px: 3,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                boxShadow: '0 10px 15px -3px rgba(59, 130, 246, 0.3)',
                color: '#ffffff'
              },
              '&:disabled': {
                background: '#d1d5db',
                color: '#9ca3af'
              }
            }}
          >
            {creating ? 'Creating...' : 'Create Sprint'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DesignSprintsListPage; 